import './index.css'
import { useState, useEffect } from 'react'
import { Pencil, Trash2, FolderTree, MoreVertical, Plus, X } from 'lucide-react'
import { fmt, maskCurrency, digitsToAmount, amountToDigits } from '../../lib/format'
import type { Category } from '../../types'
import { getToastErrorMessage, useToast } from '../Toast'

interface Props {
  categories: Category[]
  subcategories: Category[]
  onAdd: (name: string, kind: 'in' | 'out', spending_limit?: number | null) => Promise<unknown>
  onAddSubcategory: (name: string, parentId: string) => Promise<void>
  onRename: (id: string, name: string) => Promise<void>
  onSetLimit: (id: string, limit: number | null) => Promise<void>
  onDelete: (id: string) => Promise<{ error?: string }>
}

interface SectionProps {
  title: string
  kind: 'in' | 'out'
  items: Category[]
  subcategories: Category[]
  onAdd: Props['onAdd']
  onAddSubcategory: Props['onAddSubcategory']
  onRename: Props['onRename']
  onSetLimit: Props['onSetLimit']
  onDelete: Props['onDelete']
}

function digitsToLimit(digits: string): number | null {
  if (!digits) return null
  const n = digitsToAmount(digits)
  return n > 0 ? n : null
}

// ── Category add/edit modal ───────────────────────────────────────────────────

interface CategoryModalProps {
  kind: 'in' | 'out'
  initial?: Category
  onSave: (name: string, limit: number | null) => Promise<void>
  onClose: () => void
}

function CategoryModal({ kind, initial, onSave, onClose }: CategoryModalProps) {
  const toast = useToast()
  const [name, setName]         = useState(initial?.name ?? '')
  const [limitDigits, setLimit] = useState(
    initial?.spending_limit != null ? amountToDigits(initial.spending_limit) : ''
  )
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  async function handleSave() {
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Nome obrigatório.')
      toast.warning('Informe o nome da categoria.')
      return
    }
    setSaving(true); setError(null)
    try {
      await onSave(trimmed, kind === 'out' ? digitsToLimit(limitDigits) : null)
      onClose()
    } catch {
      setError('Erro ao salvar. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  const isEdit = !!initial

  return (
    <div className="bc-cat-overlay" onClick={onClose}>
      <div className="bc-cat-modal" onClick={e => e.stopPropagation()}>
        <div className="bc-cat-modal__header">
          <h3 className="bc-cat-modal__title">
            {isEdit ? 'Editar categoria' : kind === 'out' ? 'Nova saída' : 'Nova entrada'}
          </h3>
          <button className="bc-cat-modal__close" aria-label="Fechar" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="bc-cat-modal__body">
          <div className="bc-cat-modal__field">
            <label className="bc-cat-modal__label">Nome</label>
            <input
              className="bc-input"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSave() }}
              maxLength={50}
              autoFocus
              placeholder={kind === 'out' ? 'Ex.: Alimentação' : 'Ex.: Salário'}
            />
          </div>

          {kind === 'out' && (
            <div className="bc-cat-modal__field">
              <label className="bc-cat-modal__label">
                Limite mensal <span style={{ fontWeight: 400, color: 'var(--ink-4)' }}>(opcional)</span>
              </label>
              <div className="bc-currency">
                <span className="bc-currency__prefix">R$</span>
                <input
                  value={maskCurrency(limitDigits)}
                  onChange={e => setLimit(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={e => { if (e.key === 'Enter') handleSave() }}
                  placeholder="0,00"
                  inputMode="numeric"
                />
              </div>
            </div>
          )}

          {error && <p className="bc-cat-modal__error">{error}</p>}
        </div>

        <div className="bc-cat-modal__footer">
          <button className="bc-btn bc-btn--ghost bc-btn--sm" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button className="bc-btn bc-btn--primary bc-btn--sm" onClick={handleSave} disabled={saving || !name.trim()}>
            {saving ? '…' : isEdit ? 'Salvar' : 'Adicionar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Subcategory modal ─────────────────────────────────────────────────────────

interface SubcategoryModalProps {
  parent: Category
  subcategories: Category[]
  onAdd: (name: string, parentId: string) => Promise<void>
  onDelete: (id: string) => Promise<{ error?: string }>
  onClose: () => void
}

function SubcategoryModal({ parent, subcategories, onAdd, onDelete, onClose }: SubcategoryModalProps) {
  const toast = useToast()
  const [newName, setNewName] = useState('')
  const [adding, setAdding]   = useState(false)
  const subs = subcategories.filter(s => s.parent_id === parent.id)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  async function handleAdd() {
    const name = newName.trim()
    if (!name) {
      toast.warning('Informe o nome da subcategoria.')
      return
    }
    setAdding(true)
    try {
      await onAdd(name, parent.id)
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao criar subcategoria.'))
    } finally {
      setAdding(false)
    }
    setNewName('')
  }

  return (
    <div className="bc-cat-overlay" onClick={onClose}>
      <div className="bc-cat-modal" onClick={e => e.stopPropagation()}>
        <div className="bc-cat-modal__header">
          <h3 className="bc-cat-modal__title">Subcategorias — {parent.name}</h3>
          <button className="bc-cat-modal__close" aria-label="Fechar" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="bc-cat-modal__body">
          {subs.length === 0 ? (
            <p className="bc-cat-sub-empty">Nenhuma subcategoria ainda.</p>
          ) : (
            <ul className="bc-cat-sub-list">
              {subs.map(s => (
                <li key={s.id} className="bc-cat-sub-item">
                  <span className="bc-cat-sub-item__name">{s.name}</span>
                  <button
                    className="bc-cat-sub-item__del"
                    aria-label={`Excluir ${s.name}`}
                    onClick={() => onDelete(s.id)}
                  >
                    <X size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="bc-cat-sub-divider" />

          <div className="bc-cat-sub-add">
            <input
              className="bc-input"
              placeholder="Nova subcategoria..."
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAdd() }}
              maxLength={50}
              style={{ flex: 1 }}
              autoFocus={subs.length === 0}
            />
            <button
              className="bc-btn bc-btn--primary bc-btn--sm"
              onClick={handleAdd}
              disabled={adding || !newName.trim()}
            >
              {adding ? '…' : 'Adicionar'}
            </button>
          </div>
        </div>

        <div className="bc-cat-modal__footer">
          <button className="bc-btn bc-btn--ghost bc-btn--sm" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  )
}

// ── Section ───────────────────────────────────────────────────────────────────

function CategorySection({ title, kind, items, subcategories, onAdd, onAddSubcategory, onRename, onSetLimit, onDelete }: SectionProps) {
  const [openMenu,    setOpenMenu]    = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [showAdd,     setShowAdd]     = useState(false)
  const [editingCat,  setEditingCat]  = useState<Category | null>(null)
  const [subCat,      setSubCat]      = useState<Category | null>(null)

  useEffect(() => {
    const close = () => setOpenMenu(null)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [])

  async function handleAdd(name: string, limit: number | null) {
    await onAdd(name, kind, limit)
  }

  async function handleEdit(name: string, limit: number | null) {
    if (!editingCat) return
    await onRename(editingCat.id, name)
    if (kind === 'out') await onSetLimit(editingCat.id, limit)
  }

  async function handleDelete(id: string) {
    setDeleteError(null)
    const result = await onDelete(id)
    if (result.error) {
      setDeleteError('Categoria em uso — remova as transações antes.')
    }
    setOpenMenu(null)
  }

  function openEdit(cat: Category) {
    setEditingCat(cat)
    setOpenMenu(null)
  }

  function openSub(cat: Category) {
    setSubCat(cat)
    setOpenMenu(null)
  }

  return (
    <>
      <div className="bc-card" style={{ marginBottom: '1rem', padding: 'var(--sp-4)' }}>
        <h3 style={{ fontFamily: 'var(--font-display)', marginBottom: '0.75rem', fontSize: '0.875rem' }}>
          {title}
        </h3>

        {items.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 0.75rem' }}>
            {items.map(cat => (
              <li key={cat.id} className={`bc-cat-item${openMenu === cat.id ? ' is-expanded' : ''}`}>
                <div className="bc-cat-row">
                  <span className="bc-cat-row__name">{cat.name}</span>
                  {kind === 'out' && (
                    <span className="bc-cat-row__limit">
                      {cat.spending_limit != null ? `Limite: ${fmt(cat.spending_limit)}` : 'Sem limite'}
                    </span>
                  )}

                  {/* Desktop inline actions — visible on row hover */}
                  <div className="bc-cat-row__actions">
                    {kind === 'out' && (
                      <button
                        className="bc-btn bc-btn--icon"
                        data-tip="Subcategorias"
                        onClick={() => openSub(cat)}
                      >
                        <FolderTree size={13} />
                      </button>
                    )}
                    <button
                      className="bc-btn bc-btn--icon"
                      data-tip="Editar"
                      onClick={() => openEdit(cat)}
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      className="bc-btn bc-btn--icon bc-btn--danger"
                      data-tip="Excluir"
                      onClick={() => handleDelete(cat.id)}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>

                  {/* Mobile kebab */}
                  <button
                    className={`bc-cat-kebab${openMenu === cat.id ? ' is-open' : ''}`}
                    onClick={e => { e.stopPropagation(); setOpenMenu(prev => prev === cat.id ? null : cat.id) }}
                    aria-label="Mais ações"
                  >
                    <MoreVertical size={16} />
                  </button>
                </div>

                {/* Mobile expand panel */}
                <div className="bc-cat-expand">
                  {kind === 'out' && (
                    <button className="bc-cat-expand__btn" onClick={() => openSub(cat)}>
                      <FolderTree size={14} /> Subcategorias
                    </button>
                  )}
                  <button className="bc-cat-expand__btn" onClick={() => openEdit(cat)}>
                    <Pencil size={14} /> Editar
                  </button>
                  <button className="bc-cat-expand__btn is-danger" onClick={() => handleDelete(cat.id)}>
                    <Trash2 size={14} /> Excluir
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {deleteError && (
          <p style={{ fontSize: '0.75rem', color: 'var(--expense-500)', marginBottom: '0.5rem' }}>{deleteError}</p>
        )}

        <button className="bc-cat-add-btn" onClick={() => setShowAdd(true)}>
          <Plus size={14} />
          Cadastrar categoria
        </button>
      </div>

      {/* Modals */}
      {showAdd && (
        <CategoryModal
          kind={kind}
          onSave={handleAdd}
          onClose={() => setShowAdd(false)}
        />
      )}

      {editingCat && (
        <CategoryModal
          kind={kind}
          initial={editingCat}
          onSave={handleEdit}
          onClose={() => setEditingCat(null)}
        />
      )}

      {subCat && (
        <SubcategoryModal
          parent={subCat}
          subcategories={subcategories}
          onAdd={onAddSubcategory}
          onDelete={onDelete}
          onClose={() => setSubCat(null)}
        />
      )}
    </>
  )
}

// ── Root ──────────────────────────────────────────────────────────────────────

export default function CategoryManager({ categories, subcategories, onAdd, onAddSubcategory, onRename, onSetLimit, onDelete }: Props) {
  const outCats = categories.filter(c => c.kind === 'out')
  const inCats  = categories.filter(c => c.kind === 'in')

  return (
    <div>
      <h2 style={{ fontFamily: 'var(--font-display)', marginBottom: '1rem', fontSize: '1rem' }}>
        Categorias
      </h2>
      <CategorySection title="Saídas" kind="out" items={outCats} subcategories={subcategories}
        onAdd={onAdd} onAddSubcategory={onAddSubcategory} onRename={onRename} onSetLimit={onSetLimit} onDelete={onDelete} />
      <CategorySection title="Entradas" kind="in" items={inCats} subcategories={subcategories}
        onAdd={onAdd} onAddSubcategory={onAddSubcategory} onRename={onRename} onSetLimit={onSetLimit} onDelete={onDelete} />
    </div>
  )
}
